import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import LeadForm from "@/components/LeadForm";
import { ArrowRight, CheckCircle, Search, FileText, Shield, ChevronDown } from "lucide-react";

function ExampleDeal() {
  return (
    <div data-testid="example-deal" className="bg-[#0b132b] rounded-2xl border border-white/10 p-6 max-w-sm mx-auto">
      <div className="flex items-center justify-between mb-4">
        <span className="text-emerald-400 text-xs font-bold uppercase tracking-wider">BRR Strategy</span>
        <span className="text-amber-400 text-[10px] border border-amber-500/30 rounded-full px-2 py-0.5">Example only, not for sale</span>
      </div>
      <p className="text-white font-bold text-lg mb-1">3-Bed Terrace, Co. Down</p>
      <div className="space-y-2 text-sm mt-4">
        {[["Purchase price", "£72,000"], ["Refurb estimate", "£18,000 – £22,000"],
          ["Estimated value after works", "£115,000 (est.)"],
          ["Monthly rent", "£625 (est.)"], ["Gross yield", "10.4% (est.)"],
          ["Estimated profit on flip", "£21,000 – £25,000 (est.)"],
        ].map(([l, v]) => (
          <div key={l} className="flex justify-between border-b border-white/5 pb-1.5">
            <span className="text-slate-400">{l}</span>
            <span className="text-white font-medium">{v}</span>
          </div>
        ))}
      </div>
      <p className="text-slate-600 text-[10px] mt-4 text-center">All figures are estimates for illustration. Past analyses; not available for purchase.</p>
    </div>
  );
}

function FAQ() {
  const [open, setOpen] = useState(null);
  const items = [
    { q: "What's a sourcing fee?", a: "A fee payable on completion of your purchase. A reservation deposit (refundable during the 14-day due diligence period) secures the deal while you verify everything." },
    { q: "Do you sell to anyone?", a: "Only registered investors who have completed ID verification, have a solicitor in place and can provide proof of funds. We work with serious buyers only." },
    { q: "Are these real deals?", a: "Examples shown on this page are past analyses for illustration only. Live deals are shared exclusively with registered investors who match the criteria." },
    { q: "How does the matching work?", a: "You tell us exactly what you're looking for. When we source a deal that fits your brief, we contact you first. No spam, no irrelevant deals." },
    { q: "What areas do you cover?", a: "All of Northern Ireland. We focus on areas with strong rental yields and capital growth potential." },
  ];
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
            <span className="text-[#0b132b] font-medium text-sm">{it.q}</span>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <div className="px-5 pb-4 text-slate-600 text-sm leading-relaxed">{it.a}</div>}
        </div>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState(null);
  const [params] = useSearchParams();
  const [utm, setUtm] = useState({});

  useEffect(() => { document.title = "DealDeck | Property deals in Northern Ireland"; }, []);

  useEffect(() => {
    const fromUrl = { source: params.get("utm_source") || "", medium: params.get("utm_medium") || "",
             campaign: params.get("utm_campaign") || "", content: params.get("utm_content") || "",
             post: params.get("utm_post") || params.get("utm_content") || "" };
    if (Object.values(fromUrl).some(Boolean)) {
      sessionStorage.setItem("dd_utm", JSON.stringify(fromUrl));
      setUtm(fromUrl);
    } else {
      setUtm(JSON.parse(sessionStorage.getItem("dd_utm") || "{}"));
    }
  }, [params]);

  if (showForm && !result) return <LeadForm utm={utm} onComplete={r => setResult(r)} onBack={() => setShowForm(false)} />;

  if (result) return (
    <div className="min-h-screen bg-[#0b132b] flex items-center justify-center px-6" data-testid="thank-you">
      <div className="text-center max-w-sm animate-slide-up">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <h1 data-testid="thank-you-message" className="text-2xl font-bold text-white mb-3">Thanks, one of our team will be in touch shortly.</h1>
        <p className="text-slate-500 text-xs mt-8">DealDeck</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero */}
      <section className="bg-[#0b132b] px-6 pt-14 pb-16 text-center">
        <p data-testid="landing-brand" className="text-emerald-500 text-xs font-bold uppercase tracking-[0.2em] mb-4">DealDeck</p>
        <h1 className="text-white text-3xl sm:text-4xl font-extrabold leading-tight mb-4 max-w-md mx-auto" style={{fontFamily:"'Syne',sans-serif"}}>
          Investor-ready property deals in Northern Ireland, sourced to your brief.
        </h1>
        <p className="text-slate-400 text-base max-w-xs mx-auto mb-8">
          Tell us what you buy. We find it, analyse it and package it.
        </p>
        <button data-testid="hero-cta" onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-base px-8 py-4 rounded-full transition-all shadow-lg shadow-emerald-500/20 active:scale-95">
          Tell us what you're looking for <ArrowRight className="w-5 h-5" />
        </button>
      </section>

      {/* How It Works */}
      <section className="bg-white px-6 py-14">
        <h2 className="text-[#0b132b] text-xl font-bold text-center mb-10" style={{fontFamily:"'Syne',sans-serif"}}>How it works</h2>
        <div className="max-w-md mx-auto space-y-8">
          {[{ icon: Search, title: "Tell us your criteria", desc: "Strategy, area and budget. Takes 30 seconds." },
            { icon: FileText, title: "We source and analyse", desc: "PropertyPal, off-market, auctions. Full deal pack with comparables, yield and refurb costs." },
            { icon: Shield, title: "Reserve and verify", desc: "Refundable reservation fee. 14 days for your own due diligence before you commit." },
          ].map((s, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <s.icon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[#0b132b] font-bold text-sm">{s.title}</p>
                <p className="text-slate-500 text-sm mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Example Deal */}
      <section className="bg-slate-50 px-6 py-14">
        <h2 className="text-[#0b132b] text-xl font-bold text-center mb-2" style={{fontFamily:"'Syne',sans-serif"}}>Example deal analysis</h2>
        <p className="text-slate-500 text-sm text-center mb-8">This is what a deal pack looks like. For illustration only.</p>
        <ExampleDeal />
      </section>

      {/* CTA */}
      <section className="bg-[#0b132b] px-6 py-12 text-center">
        <p className="text-white text-lg font-bold mb-2" style={{fontFamily:"'Syne',sans-serif"}}>Ready to find your next deal?</p>
        <p className="text-slate-400 text-sm mb-6">Register in 30 seconds. No obligation.</p>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-8 py-4 rounded-full transition-all active:scale-95">
          Tell us what you're looking for <ArrowRight className="w-5 h-5" />
        </button>
      </section>

      {/* FAQ */}
      <section className="bg-white px-6 py-14">
        <h2 className="text-[#0b132b] text-xl font-bold text-center mb-8" style={{fontFamily:"'Syne',sans-serif"}}>Frequently asked questions</h2>
        <div className="max-w-md mx-auto"><FAQ /></div>
      </section>

      {/* Footer — links only to Privacy Policy and Terms, nothing into the app */}
      <footer className="bg-[#0b132b] border-t border-white/5 px-6 py-8 text-center space-y-2">
        <p className="text-white text-sm font-bold">DealDeck</p>
        <p className="text-slate-500 text-xs">ICO Registration: ZC034426</p>
        <div className="flex justify-center gap-4 text-xs">
          <a href="/privacy" data-testid="footer-privacy-link" className="text-emerald-500 hover:text-emerald-400">Privacy Policy</a>
          <a href="/terms" data-testid="footer-terms-link" className="text-emerald-500 hover:text-emerald-400">Terms</a>
        </div>
        <p className="text-slate-600 text-[10px] pt-2 max-w-xs mx-auto">
          DealDeck is a trading name of RJL Property Solutions Ltd (NI744060).
        </p>
      </footer>
    </div>
  );
}
